/**
 * Google Analytics 4 (gtag) shared config for the OnArk funnel.
 *
 * The base loader lives in three places: `components/global/HeadScripts.tsx`
 * (legacy Pages Router), `marketing/components/head-scripts.tsx` (the
 * `hi.onark.app` marketing app), and — for the App Router signup funnel, which
 * neither HeadScripts reaches — `app/_components/Ga4Pixel.tsx`. Every one of
 * them imports the id below rather than re-typing it, exactly like the Meta
 * pixel id (see `utils/metaPixel.ts`). `__tests__/ga4-coverage.test.ts` asserts
 * the id is written down once and imported everywhere else, so the three
 * loaders cannot drift onto different properties.
 */
import { GPC_ACTIVE } from './gpc';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * The one OnArk GA4 measurement id — same property as the marketing site so a
 * `hi.onark.app` → `/sign-up` journey is a single continuous session, and so the
 * "OnArk (web) sign_up" conversion Google Ads imports from GA4 counts the
 * `sign_up` event `app/sign-up/card/CardStepPage.tsx` fires at the card step.
 *
 * Hard-coded rather than env-driven on purpose: it must match the marketing and
 * legacy loaders exactly, and it is not a secret — it ships to the browser in
 * every gtag snippet regardless.
 */
export const GA_MEASUREMENT_ID = 'G-Z7RVNLD4FY' as const;

/**
 * Google Consent Mode default, spliced into all three gtag init snippets
 * right after `gtag('js', ...)` and before `gtag('config', ...)`. Only sets a
 * DENIED default when GPC is active — it never grants, so absence of GPC
 * leaves gtag's own (unset) default behavior unchanged. This is the one place
 * that restricts ad-linked signals (Google Ads conversions imported from GA4
 * events, e.g. the `sign_up` event at `app/sign-up/card/CardStepPage.tsx`) for
 * the REST of the page's gtag calls, without having to gate each call site.
 */
export const GA_CONSENT_DEFAULT_SNIPPET =
  `if (${GPC_ACTIVE}) { gtag('consent', 'default', { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' }); }` as const;
